import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3, Users, Building2, Ticket, Inbox, DollarSign, CreditCard, MessageSquare, Megaphone, Settings, Trash2, Plus, Radio,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import { Button, Card, Input, Badge } from "@/components/ui";

const TABS = [
  ["overview", BarChart3, "نظرة عامة", "Overview"],
  ["users", Users, "المستخدمون", "Users"],
  ["firms", Building2, "المكاتب", "Firms"],
  ["promos", Ticket, "العروض", "Promos"],
  ["requests", Inbox, "طلبات التفعيل", "Requests"],
  ["pricing", DollarSign, "الأسعار", "Pricing"],
  ["payments", CreditCard, "طرق الدفع", "Payments"],
  ["messages", MessageSquare, "الرسائل", "Messages"],
  ["banners", Megaphone, "الإعلانات", "Banners"],
  ["adsnet", Radio, "شبكة الإعلانات", "Ads Network"],
  ["settings", Settings, "الإعدادات", "Settings"],
];

function Overview({ isAr }) {
  const { data, loading, error } = useDeliverable(() => api.adminStats(), []);
  const flat = [];
  const walk = (obj, prefix = "") => {
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (typeof v === "number") flat.push([prefix + k, v]);
      else if (v && typeof v === "object" && !Array.isArray(v)) walk(v, `${prefix}${k} · `);
    });
  };
  walk(data);
  return (
    <DeliverableState loading={loading} error={error} isAr={isAr}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {flat.map(([k, v]) => (
          <Card key={k} className="p-4"><div className="text-2xl font-black text-primary">{Number(v).toLocaleString()}</div><div className="text-xs capitalize text-muted-foreground">{k.replace(/_/g, " ")}</div></Card>
        ))}
        {!flat.length && <p className="text-sm text-muted-foreground">—</p>}
      </div>
    </DeliverableState>
  );
}

function UsersPanel({ isAr }) {
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useDeliverable(() => api.adminListUsers(q, 0, 50), [q]);
  const items = data?.items || data || [];
  const act = async (u, action) => { try { await api.adminSubscriptionAction(u.id, action); toast.success("OK"); reload(); } catch (e) { toast.error(String(e.message || e)); } };
  const gift = async (u) => {
    const days = parseInt(window.prompt(isAr ? "إهداء Pro لعدد أيام (مثال 30، أو 3650 مدى الحياة)" : "Gift Pro for how many days? (e.g. 30, or 3650 for lifetime)", "30"), 10);
    if (!days || days < 1) return;
    try { await api.adminSubscriptionAction(u.id, "grant", days); toast.success(isAr ? `تم إهداء Pro لمدة ${days} يوم` : `Gifted Pro for ${days} days`); reload(); }
    catch (e) { toast.error(String(e.message || e)); }
  };
  const message = async (u) => {
    const title = window.prompt(isAr ? "عنوان الرسالة" : "Message title"); if (!title) return;
    const body = window.prompt(isAr ? "نص الرسالة" : "Message body"); if (!body) return;
    try { await api.adminMessageUser(u.id, title, body); toast.success(isAr ? "تم إرسال الرسالة" : "Message sent"); }
    catch (e) { toast.error(String(e.message || e)); }
  };
  const del = async (u) => { if (!window.confirm(isAr ? "حذف المستخدم؟" : "Delete user?")) return; try { await api.adminDeleteUser(u.id); reload(); } catch (e) { toast.error(String(e.message || e)); } };
  return (
    <>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isAr ? "بحث بالبريد/الاسم" : "Search email/name"} className="mb-4 max-w-sm" data-testid="admin-user-search" />
      <DeliverableState loading={loading} error={error} isAr={isAr}>
        <div className="space-y-2">
          {items.map((u) => (
            <Card key={u.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div className="min-w-0">
                <div className="truncate font-bold">{u.full_name || u.email}</div>
                <div className="text-xs text-muted-foreground">{u.email} · <Badge tone={u.role === "admin" ? "danger" : "muted"}>{u.role}</Badge> · <Badge tone={u.subscription?.tier === "pro" ? "success" : "muted"}>{u.subscription?.tier || "free"}</Badge></div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => gift(u)} data-testid={`admin-gift-${u.id}`}>{isAr ? "إهداء Pro" : "Gift Pro"}</Button>
                <Button size="sm" variant="outline" onClick={() => act(u, "cancel")}>{isAr ? "إلغاء" : "Cancel"}</Button>
                <Button size="sm" variant="outline" onClick={() => message(u)} data-testid={`admin-message-${u.id}`}><MessageSquare size={14} /> {isAr ? "رسالة" : "Message"}</Button>
                <button onClick={() => del(u)} className="grid h-9 w-9 place-items-center rounded-lg text-red-400 hover:bg-destructive/15"><Trash2 size={15} /></button>
              </div>
            </Card>
          ))}
        </div>
      </DeliverableState>
    </>
  );
}

function FirmsPanel({ isAr }) {
  const { data, loading, error, reload } = useDeliverable(() => api.adminListFirms("", 0, 50), []);
  const items = data?.items || [];
  const setSeats = async (f) => { const v = parseInt(window.prompt(isAr ? "عدد المقاعد" : "Max seats", f.max_seats || 5), 10); if (!v) return; try { await api.adminSetFirmSeats(f.id, v); reload(); } catch (e) { toast.error(String(e.message || e)); } };
  const del = async (f) => { if (!window.confirm(isAr ? "حذف المكتب؟" : "Delete firm?")) return; try { await api.adminDeleteFirm(f.id); reload(); } catch (e) { toast.error(String(e.message || e)); } };
  return (
    <DeliverableState loading={loading} error={error} isAr={isAr}>
      <div className="space-y-2">
        {items.map((f) => (
          <Card key={f.id} className="flex items-center justify-between gap-2 p-3">
            <div><div className="font-bold">{f.name}</div><div className="text-xs text-muted-foreground">{isAr ? "المقاعد" : "seats"}: {f.seats_used ?? "?"}/{f.max_seats}</div></div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setSeats(f)}>{isAr ? "المقاعد" : "Seats"}</Button>
              <button onClick={() => del(f)} className="grid h-9 w-9 place-items-center rounded-lg text-red-400 hover:bg-destructive/15"><Trash2 size={15} /></button>
            </div>
          </Card>
        ))}
        {!items.length && <p className="text-sm text-muted-foreground">—</p>}
      </div>
    </DeliverableState>
  );
}

function PromosPanel({ isAr }) {
  const { data, loading, error, reload } = useDeliverable(() => api.adminListPromos(), []);
  const items = data?.items || data || [];
  const create = async () => { try { await api.adminCreatePromo({ type: "grant_pro", duration_days: 30, max_redemptions: 100 }); reload(); toast.success(isAr ? "تم إنشاء عرض" : "Promo created"); } catch (e) { toast.error(String(e.message || e)); } };
  return (
    <>
      <Button className="mb-4" size="sm" onClick={create} data-testid="admin-create-promo"><Plus size={15} /> {isAr ? "عرض جديد (Pro 30 يوم)" : "New promo (Pro 30d)"}</Button>
      <DeliverableState loading={loading} error={error} isAr={isAr}>
        <div className="space-y-2">
          {items.map((p) => (
            <Card key={p.code} className="flex items-center justify-between gap-2 p-3">
              <div><div className="mono font-bold">{p.code}</div><div className="text-xs text-muted-foreground">{p.type} · {p.redemptions ?? 0}/{p.max_redemptions ?? "∞"}</div></div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={async () => { try { await api.adminTogglePromo(p.code, !p.active); reload(); } catch (e) { toast.error(String(e.message || e)); } }}>{p.active ? (isAr ? "تعطيل" : "Disable") : (isAr ? "تفعيل" : "Enable")}</Button>
                <button onClick={async () => { try { await api.adminDeletePromo(p.code); reload(); } catch (e) { toast.error(String(e.message || e)); } }} className="grid h-9 w-9 place-items-center rounded-lg text-red-400 hover:bg-destructive/15"><Trash2 size={15} /></button>
              </div>
            </Card>
          ))}
          {!items.length && <p className="text-sm text-muted-foreground">—</p>}
        </div>
      </DeliverableState>
    </>
  );
}

function RequestsPanel({ isAr }) {
  const { data, loading, error, reload } = useDeliverable(() => api.adminListActivationRequests(), []);
  const items = data?.items || data || [];
  return (
    <DeliverableState loading={loading} error={error} isAr={isAr}>
      <div className="space-y-2">
        {items.map((r) => (
          <Card key={r.id} className="flex items-center justify-between gap-2 p-3">
            <div><div className="font-bold">{r.user_email || r.email || r.user_id}</div><div className="text-xs text-muted-foreground">{r.plan || r.tier} · <Badge tone={r.status === "approved" ? "success" : r.status === "rejected" ? "danger" : "warning"}>{r.status}</Badge></div></div>
            {r.status === "pending" && (
              <div className="flex gap-1.5">
                <Button size="sm" onClick={async () => { try { await api.adminApproveActivation(r.id); reload(); } catch (e) { toast.error(String(e.message || e)); } }}>{isAr ? "قبول" : "Approve"}</Button>
                <Button size="sm" variant="danger" onClick={async () => { try { await api.adminRejectActivation(r.id); reload(); } catch (e) { toast.error(String(e.message || e)); } }}>{isAr ? "رفض" : "Reject"}</Button>
              </div>
            )}
          </Card>
        ))}
        {!items.length && <p className="text-sm text-muted-foreground">—</p>}
      </div>
    </DeliverableState>
  );
}

function PricingPanel({ isAr }) {
  const { data, loading, error } = useDeliverable(() => api.adminGetPricing(), []);
  const [tiers, setTiers] = useState(null);
  const [ppe, setPpe] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (data) { setTiers(data.tiers || {}); setPpe(data.pay_per_export || {}); setCurrency(data.currency || "USD"); }
  }, [data]);
  const setPrice = (tier, field, v) => setTiers((t) => ({ ...t, [tier]: { ...t[tier], [field]: v === "" ? 0 : parseFloat(v) } }));
  const setExport = (fmt, v) => setPpe((p) => ({ ...p, [fmt]: v === "" ? 0 : parseFloat(v) }));
  const save = async () => {
    setBusy(true);
    try { await api.adminUpdatePricing({ tiers, pay_per_export: ppe, currency }); toast.success(isAr ? "تم حفظ الأسعار" : "Pricing saved"); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(false); }
  };
  return (
    <DeliverableState loading={loading} error={error} isAr={isAr}>
      {tiers && (
        <div className="space-y-4">
          <Card>
            <div className="mb-3 flex items-center gap-2">
              <p className="text-sm font-black">{isAr ? "العملة" : "Currency"}</p>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="admin-currency" className="rounded-lg border border-input bg-card px-2 py-1.5 text-xs font-bold">
                {["USD", "SAR", "AED", "EGP", "EUR", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {Object.entries(tiers).map(([tier, cfg]) => (
                <div key={tier} className="rounded-xl border border-border bg-card/40 p-3">
                  <p className="mb-2 font-bold capitalize">{isAr ? (cfg.label_ar || tier) : (cfg.label_en || tier)}</p>
                  <label className="mb-1 block text-xs text-muted-foreground">{isAr ? "سعر شهري" : "Monthly price"}</label>
                  <Input type="number" step="0.01" value={cfg.monthly_price ?? 0} onChange={(e) => setPrice(tier, "monthly_price", e.target.value)} data-testid={`admin-price-${tier}-monthly`} className="mb-2" />
                  <label className="mb-1 block text-xs text-muted-foreground">{isAr ? "سعر سنوي" : "Yearly price"}</label>
                  <Input type="number" step="0.01" value={cfg.yearly_price ?? 0} onChange={(e) => setPrice(tier, "yearly_price", e.target.value)} data-testid={`admin-price-${tier}-yearly`} />
                </div>
              ))}
            </div>
          </Card>
          {ppe && Object.keys(ppe).length > 0 && (
            <Card>
              <p className="mb-3 text-sm font-black">{isAr ? "سعر التصدير للمرة الواحدة" : "Pay-per-export"}</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {Object.entries(ppe).map(([fmt, price]) => (
                  <div key={fmt}>
                    <label className="mb-1 block text-xs uppercase text-muted-foreground">{fmt}</label>
                    <Input type="number" step="0.01" value={price ?? 0} onChange={(e) => setExport(fmt, e.target.value)} data-testid={`admin-ppe-${fmt}`} />
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Button loading={busy} onClick={save} data-testid="admin-pricing-save">{isAr ? "حفظ الأسعار" : "Save pricing"}</Button>
        </div>
      )}
    </DeliverableState>
  );
}

function PaymentsPanel({ isAr }) {
  const { data, loading, error, reload } = useDeliverable(() => api.adminListPaymentMethods(), []);
  const items = data?.items || data || [];
  const add = async () => { const name = window.prompt(isAr ? "اسم الطريقة" : "Method name"); if (!name) return; const details = window.prompt(isAr ? "التفاصيل" : "Details") || ""; try { await api.adminCreatePaymentMethod({ name, details }); reload(); } catch (e) { toast.error(String(e.message || e)); } };
  return (
    <>
      <Button className="mb-4" size="sm" onClick={add}><Plus size={15} /> {isAr ? "إضافة طريقة دفع" : "Add payment method"}</Button>
      <DeliverableState loading={loading} error={error} isAr={isAr}>
        <div className="space-y-2">
          {items.map((m) => (
            <Card key={m.id} className="flex items-center justify-between gap-2 p-3">
              <div><div className="font-bold">{m.name}</div><div className="text-xs text-muted-foreground">{m.details}</div></div>
              <button onClick={async () => { try { await api.adminDeletePaymentMethod(m.id); reload(); } catch (e) { toast.error(String(e.message || e)); } }} className="grid h-9 w-9 place-items-center rounded-lg text-red-400 hover:bg-destructive/15"><Trash2 size={15} /></button>
            </Card>
          ))}
          {!items.length && <p className="text-sm text-muted-foreground">—</p>}
        </div>
      </DeliverableState>
    </>
  );
}

function MessagesPanel({ isAr }) {
  const { data, loading, error } = useDeliverable(() => api.adminListContactMessages(), []);
  const items = data?.items || data || [];
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error(isAr ? "أدخل العنوان والنص" : "Enter title and body"); return; }
    setBusy(true);
    try { const r = await api.adminBroadcast(title, body, audience); toast.success(isAr ? `تم الإرسال إلى ${r.sent} مستخدم` : `Sent to ${r.sent} users`); setTitle(""); setBody(""); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <Card>
        <p className="mb-3 text-sm font-black">{isAr ? "إرسال رسالة جماعية للمستخدمين" : "Broadcast a message to users"}</p>
        <div className="mb-2 flex items-center gap-2">
          <label className="text-xs font-bold text-muted-foreground">{isAr ? "الفئة" : "Audience"}</label>
          <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="admin-broadcast-audience" className="rounded-lg border border-input bg-card px-2 py-1.5 text-xs font-bold">
            <option value="all">{isAr ? "الجميع" : "All"}</option>
            <option value="pro">{isAr ? "المشتركون Pro" : "Pro"}</option>
            <option value="free">{isAr ? "المجانيون" : "Free"}</option>
            <option value="guest">{isAr ? "الضيوف" : "Guests"}</option>
          </select>
        </div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={isAr ? "عنوان الرسالة" : "Message title"} className="mb-2" data-testid="admin-broadcast-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder={isAr ? "نص الرسالة…" : "Message body…"} className="mb-3 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm" data-testid="admin-broadcast-body" />
        <Button size="sm" loading={busy} onClick={send} data-testid="admin-broadcast-send"><MessageSquare size={15} /> {isAr ? "إرسال" : "Send"}</Button>
      </Card>
      <div>
        <p className="mb-2 text-sm font-black">{isAr ? "رسائل واردة (تواصل معنا)" : "Inbound (contact us)"}</p>
        <DeliverableState loading={loading} error={error} isAr={isAr}>
          <div className="space-y-2">
            {items.map((m) => (
              <Card key={m.id} className="p-3"><div className="font-bold">{m.subject}</div><div className="text-sm text-muted-foreground">{m.body}</div><div className="mt-1 text-xs text-muted-foreground">{m.contact_back}</div></Card>
            ))}
            {!items.length && <p className="text-sm text-muted-foreground">—</p>}
          </div>
        </DeliverableState>
      </div>
    </div>
  );
}

function BannersPanel({ isAr }) {
  const { data, loading, error, reload } = useDeliverable(() => api.adminListBanners(), []);
  const items = data?.items || data || [];
  const add = async () => { const title = window.prompt(isAr ? "عنوان الإعلان" : "Banner title"); if (!title) return; try { await api.adminCreateBanner({ title, active: true }); reload(); } catch (e) { toast.error(String(e.message || e)); } };
  return (
    <>
      <Button className="mb-4" size="sm" onClick={add}><Plus size={15} /> {isAr ? "إعلان جديد" : "New banner"}</Button>
      <DeliverableState loading={loading} error={error} isAr={isAr}>
        <div className="space-y-2">
          {items.map((b) => (
            <Card key={b.id} className="flex items-center justify-between gap-2 p-3">
              <div><div className="font-bold">{b.title}</div><div className="text-xs text-muted-foreground">{b.active ? (isAr ? "مفعّل" : "active") : (isAr ? "معطّل" : "inactive")}</div></div>
              <button onClick={async () => { try { await api.adminDeleteBanner(b.id); reload(); } catch (e) { toast.error(String(e.message || e)); } }} className="grid h-9 w-9 place-items-center rounded-lg text-red-400 hover:bg-destructive/15"><Trash2 size={15} /></button>
            </Card>
          ))}
          {!items.length && <p className="text-sm text-muted-foreground">—</p>}
        </div>
      </DeliverableState>
    </>
  );
}

function SettingsPanel({ isAr }) {
  const [busy, setBusy] = useState(null);
  const [maxDev, setMaxDev] = useState("3");
  const setFree = async (enabled) => { setBusy("free"); try { await api.adminSetFreeMode(enabled); toast.success(isAr ? "تم التحديث" : "Updated"); } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); } };
  const setMax = async () => { setBusy("max"); try { await api.adminSetMaxDevices(parseInt(maxDev, 10)); toast.success(isAr ? "تم التحديث" : "Updated"); } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); } };
  return (
    <div className="space-y-4">
      <Card>
        <p className="mb-2 text-sm font-black">{isAr ? "الوضع المجاني (Freemium)" : "Free mode (Freemium)"}</p>
        <div className="flex gap-2">
          <Button size="sm" loading={busy === "free"} onClick={() => setFree(true)}>{isAr ? "تفعيل" : "Enable"}</Button>
          <Button size="sm" variant="outline" loading={busy === "free"} onClick={() => setFree(false)}>{isAr ? "تعطيل" : "Disable"}</Button>
        </div>
      </Card>
      <Card>
        <p className="mb-2 text-sm font-black">{isAr ? "أقصى عدد أجهزة لكل حساب" : "Max devices per account"}</p>
        <div className="flex max-w-xs gap-2">
          <Input value={maxDev} onChange={(e) => setMaxDev(e.target.value)} inputMode="numeric" className="flex-1" />
          <Button size="sm" loading={busy === "max"} onClick={setMax}>{isAr ? "حفظ" : "Save"}</Button>
        </div>
      </Card>
    </div>
  );
}

function AdsToggle({ value, onChange, isAr }) {
  return (
    <button onClick={() => onChange(!value)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${value ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"}`}>
      {value ? (isAr ? "مفعّل" : "ON") : (isAr ? "معطّل" : "OFF")}
    </button>
  );
}

function AdsField({ label, value, onChange, ph }) {
  return (
    <div><p className="mb-1 text-xs text-muted-foreground">{label}</p><Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph} /></div>
  );
}

function AdsNetworkPanel({ isAr }) {
  const { data, loading, error } = useDeliverable(() => api.getAdsConfig(), []);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (data && !f) {
      setF({
        adsense_enabled: !!data.adsense_enabled,
        adsense_client_id: data.adsense_client_id || "",
        web_slots: { landing: data.web_slots?.landing || "", app: data.web_slots?.app || "" },
        admob_enabled: !!data.admob_enabled,
        admob_android_app_id: data.admob_android_app_id || "",
        admob_android_banner_unit_id: data.admob_android_banner_unit_id || "",
        admob_android_interstitial_unit_id: data.admob_android_interstitial_unit_id || "",
      });
    }
  }, [data, f]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setSlot = (k, v) => setF((s) => ({ ...s, web_slots: { ...s.web_slots, [k]: v } }));
  const save = async () => {
    setBusy(true);
    try { await api.adminUpdateAdsConfig(f); toast.success(isAr ? "تم الحفظ" : "Saved"); }
    catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };
  if (!f) return <DeliverableState loading={loading} error={error} isAr={isAr}><div /></DeliverableState>;
  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-black">Google AdSense · {isAr ? "إعلانات الويب" : "Web ads"}</p>
          <AdsToggle isAr={isAr} value={f.adsense_enabled} onChange={(v) => set("adsense_enabled", v)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <AdsField label={isAr ? "معرّف العميل (ca-pub-...)" : "Client ID (ca-pub-...)"} value={f.adsense_client_id} onChange={(v) => set("adsense_client_id", v)} ph="ca-pub-XXXXXXXXXXXXXXXX" />
          <AdsField label={isAr ? "خانة الصفحة الرئيسية (Slot)" : "Landing slot ID"} value={f.web_slots.landing} onChange={(v) => setSlot("landing", v)} ph="1234567890" />
          <AdsField label={isAr ? "خانة داخل التطبيق (Slot)" : "In-app slot ID"} value={f.web_slots.app} onChange={(v) => setSlot("app", v)} ph="0987654321" />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{isAr ? "الإعلانات تظهر فقط بعد التفعيل وإدخال المعرّفات وبعد نشر الويب." : "Ads show only after enabling + entering IDs, on the deployed web."}</p>
      </Card>
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-black">Google AdMob · {isAr ? "إعلانات الموبايل" : "Mobile ads"}</p>
          <AdsToggle isAr={isAr} value={f.admob_enabled} onChange={(v) => set("admob_enabled", v)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <AdsField label={isAr ? "معرّف تطبيق أندرويد (App ID)" : "Android App ID"} value={f.admob_android_app_id} onChange={(v) => set("admob_android_app_id", v)} ph="ca-app-pub-...~..." />
          <AdsField label={isAr ? "وحدة البانر (Banner unit)" : "Banner unit ID"} value={f.admob_android_banner_unit_id} onChange={(v) => set("admob_android_banner_unit_id", v)} ph="ca-app-pub-.../..." />
          <AdsField label={isAr ? "وحدة إعلان بيني (Interstitial)" : "Interstitial unit ID"} value={f.admob_android_interstitial_unit_id} onChange={(v) => set("admob_android_interstitial_unit_id", v)} ph="ca-app-pub-.../..." />
        </div>
        <p className="mt-2 text-xs text-amber-400">{isAr ? "AdMob يتطلب نسخة (Build) عبر زر Publish ولا يعمل في المعاينة." : "AdMob requires a native Build via Publish; it will not work in preview."}</p>
      </Card>
      <Button loading={busy} onClick={save} data-testid="admin-ads-save">{isAr ? "حفظ إعدادات الإعلانات" : "Save ad settings"}</Button>
    </div>
  );
}

const PANELS = {
  overview: Overview, users: UsersPanel, firms: FirmsPanel, promos: PromosPanel, requests: RequestsPanel,
  pricing: PricingPanel, payments: PaymentsPanel, messages: MessagesPanel, banners: BannersPanel, adsnet: AdsNetworkPanel, settings: SettingsPanel,
};

export default function AdminConsole() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = tab && PANELS[tab] ? tab : "overview";
  const Panel = PANELS[active];

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader eyebrow={isAr ? "الإدارة" : "Admin"} title={isAr ? "لوحة تحكم الإدارة" : "Admin console"} />
      <div className="abl-scroll mb-5 flex gap-2 overflow-x-auto pb-1">
        {TABS.map(([k, Icon, ar, en]) => (
          <button key={k} onClick={() => navigate(`/admin/${k}`)} data-testid={`admin-tab-${k}`}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${active === k ? "border-primary bg-primary/15 text-primary" : "border-border hover:border-primary/40"}`}>
            <Icon size={14} /> {isAr ? ar : en}
          </button>
        ))}
      </div>
      <Panel isAr={isAr} />
    </div>
  );
}
